import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import {
  SectionTitle,
  ButtonsContainer,
  Button,
  StyledTitleContainer,
  Content,
  TextContainer,
  Message,
  FirstItem,
  LastTableItem,
  TableBodyRow,
} from "./CustomersPage.styles";

import TableItem from "../../../components/Table/TableItem";
import Table from "../../../components/Table/Table";
import SearchInput from "../../../components/Search/Search";
import MiniLoader from "../../../components/MiniLoader/MiniLoader";

import openToast from "../../../utils/toast";

import {
  adminLoginToBusinessUser,
  getCustomers,
} from "../../../store/slices/businessUser/asyncThunks";
import {
  selectBusinessUserInfo,
  selectedCustomers,
  setBusinessUserInfo,
} from "../../../store/slices/businessUser/slice";
import { getBusinessRulesVariables } from "../../../store/slices/common/asyncThunks";
import { getCountFavoriteMarketplaceItems } from "../../../store/slices/marketplace/asyncThunks";
import { ReactComponent as Loading } from "../../../assets/icons/loader-white.svg";
import { selectAdminLoginToBusinessUserPending } from "../../../store/slices/auth/slice";
import { checkPermission } from "../../../utils/utils";
import { businessUserRoleAction } from "../../../utils/constants";

const CustomersPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const page = queryParams.get("page");

  const { data, total, loading, perPage } = useSelector(selectedCustomers);
  const adminLoginToBusinessUserPending = useSelector(
    selectAdminLoginToBusinessUserPending
  );
  const [clickedUserId, setClickedUserId] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const businessUser = useSelector(selectBusinessUserInfo);

  const toastId = useRef(null);
  const inputRef = useRef(null);

  const tableHeader = [
    {
      label: `${t("email")}`,
      sortable: true,
      key: "email",
    },
    {
      label: `${t("full-name")}`,
      sortable: true,
      key: "firstName",
    },
    {
      label: `${t("mobile-number")}`,
      sortable: true,
      key: "mobileNumber",
    },

    {
      label: "",
      sortable: false,
      key: "button",
    },
  ];

  useEffect(() => {
    dispatch(getCustomers({ page: 1 }))
      .unwrap()
      .then(() => {})
      .catch((error) => {
        openToast(toastId, "error-search", "error", t(error.message));
      });
  }, []);

  const handlePageChange = (pageNumber) => {
    dispatch(getCustomers({ page: pageNumber }));
    const query = { page: pageNumber };
    const queryString = new URLSearchParams(query).toString();
    const url = `/admin-portal/customers?${queryString}`;

    navigate(url);
  };

  const changeHandler = useCallback((text) => {
    dispatch(getCustomers({ page: 1, search: text }))
      .unwrap()
      .then(() => {
        inputRef.current.focus();
      })
      .catch((error) => {
        openToast(toastId, "error-search", "error", t(error.message));
      });
  }, []);

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler]
  );
  const handleChange = (event) => {
    debouncedChangeHandler(event.target.value);
  };

  const handleLogin = (businessUserId) => {
    dispatch(adminLoginToBusinessUser(businessUserId))
      .unwrap()
      .then((response) => {
        setClickedUserId(null);

        window.history.replaceState({}, "", "");

        const { businessUser } = response;
        dispatch(setBusinessUserInfo(businessUser));
        dispatch(getBusinessRulesVariables());
        dispatch(getCountFavoriteMarketplaceItems());
      })
      .catch(async (err) => {
        openToast(toastId, "login", "error", t(err.message));
      });
  };

  return (
    <>
      <StyledTitleContainer>
        <SectionTitle>{t("customers")}</SectionTitle>

        <ButtonsContainer>
          <SearchInput
            inputRef={inputRef}
            placeholderColor={true}
            placeholder={t("search-by-name-or-email")}
            value={searchTerm}
            onChange={(e) => {
              handleChange(e);
              setSearchTerm(e.target.value);
            }}
            disabled={data?.length === 0 && searchTerm === ""}
          />
          {checkPermission(businessUser, businessUserRoleAction.post_users) && (
            <Button
              onClick={() => {
                navigate("/admin-portal/customers/new-customer");
              }}
            >
              {t("new-customer")}
            </Button>
          )}
        </ButtonsContainer>
      </StyledTitleContainer>

      {!loading ? (
        <>
          {total <= 0 ? (
            <Content>
              <TextContainer>
                <Message>{t("you-currently-have-no-customers")}</Message>
              </TextContainer>
            </Content>
          ) : (
            <Table
              marginTop={40}
              headerInformation={tableHeader}
              totalCount={total}
              currentPage={+page}
              setPage={handlePageChange}
              perPage={perPage}
              param={"page"}
              noDataContent={t("no-data")}
              lastColumnAligned
              centerText
            >
              {data?.map((val, key) => {
                return (
                  <TableBodyRow key={key} $offClick>
                    <FirstItem
                      hoverText={val?.email}
                      length={18}
                      showHover
                      data={
                        val.email && val.email.length > 18
                          ? val.email.slice(0, 18) + "..."
                          : val.email || "/"
                      }
                    />
                    <TableItem data={val.fullName || "/"} center />
                    <TableItem data={val.mobileNumber || "/"} center />
                    {checkPermission(
                      businessUser,
                      businessUserRoleAction.post_login_user
                    ) && (
                      <LastTableItem
                        data={
                          adminLoginToBusinessUserPending &&
                          clickedUserId === val.id ? (
                            <Button style={{ width: 100 }} $disabled>
                              <Loading height={25} />
                            </Button>
                          ) : (
                            <Button
                              $disabled={adminLoginToBusinessUserPending}
                              style={{ width: 100 }}
                              onClick={() => {
                                if (!adminLoginToBusinessUserPending) {
                                  handleLogin(val.id);
                                  setClickedUserId(val.id);
                                }
                              }}
                            >
                              {t("login")}
                            </Button>
                          )
                        }
                        $offClick
                      />
                    )}
                  </TableBodyRow>
                );
              })}
            </Table>
          )}
        </>
      ) : (
        <MiniLoader />
      )}
    </>
  );
};

export default CustomersPage;
